import React, { useContext, useState } from "react";

import machine from "../machine.js";
import utils from "../utils.js";

const MachineContext = React.createContext();
export function useMachine() {
	return useContext(MachineContext);
}

export function MachineProvider({ children }) {
	const [name, setName] = useState(machine.name);
	const [alphabet, setAlphabet] = useState([...machine.alphabet]);
	const [states, setStates] = useState([...machine.states]);
	const [transitions, setTransitions] = useState([...machine.transitions]);

	const [initialStateID, setInitialStateID] = useState(
		machine.initialStateID
	);
	const [acceptStatesIDs, setAcceptStatesIDs] = useState([
		...machine.acceptStatesIDs,
	]);

	const [autoAlphabet, setAutoAlphabet] = useState(true);

	const updateAlphabet = (string) => {
		if (string?.length > 0) {
			const newSymbols = symbolsFromString(string);
			updateTransitions(newSymbols);
			setAlphabet([...newSymbols]);
		}
	};

	const addState = (state) => {
		setStates((states) => [...states, state]);
	};
	const addTransition = (transition) => {
		if (repitedTransition(transition)) return;
		setTransitions((transitions) => [...transitions, transition]);
	};

	const repitedTransition = (transition) => {
		const startNodeID = transition.start;
		const endNodeID = transition.end;

		// Checking if there is a transition already created
		// between the same states in the same direction
		return (
			transitions.findIndex(
				(t) => t.start === startNodeID && t.end === endNodeID
			) !== -1
		);
	};

	const renameMachine = (name) => {
		setName(name);
	};
	const renameState = (id, name) => {
		let newStates = [...states];
		newStates[utils.IDToIndex(states, id)].name = name;
		setStates([...newStates]);
	};
	const renameTransition = (id, symbols) => {
		let newSymbols = symbolsFromString(symbols);
		if (autoAlphabet) {
			completeAlphabet(newSymbols);
		} else {
			newSymbols = filterSymbols(newSymbols);
		}
		if (newSymbols?.length > 0) {
			let newTransitions = [...transitions];
			newTransitions[utils.IDToIndex(transitions, id)].symbols =
				newSymbols;
			setTransitions([...newTransitions]);
		}
	};

	const completeAlphabet = (symbols) => {
		const newAlphabet = [...alphabet];
		symbols.forEach((symbol) => {
			if (!newAlphabet.includes(symbol)) {
				newAlphabet.push(symbol);
			}
		});
		setAlphabet([...newAlphabet]);
	};

	const symbolsFromString = (string) => {
		// Eliminación de espacios en blanco
		const newValue = string.replace(/\s+/g, "");
		// Extracción de los símbolos
		let newSymbols = newValue.split(",");

		newSymbols = newSymbols.filter(function (symbol, index) {
			return (
				symbol !== "" && // No se aceptan símbolos vacíos
				symbol.length <= 1 && // No se aceptan cadenas
				newSymbols.indexOf(symbol) === index // No se aceptan símbolos repetidos
			);
		});

		return newSymbols;
	};

	const filterSymbols = (symbols) => {
		return symbols.filter((symbol) => alphabet.includes(symbol));
	};

	const setInitialState = (id) => {
		setInitialStateID(id);
	};

	const addAcceptState = (id) => {
		setAcceptStatesIDs([...acceptStatesIDs, id]);
	};
	const removeAcceptState = (id) => {
		setAcceptStatesIDs(acceptStatesIDs.filter((e) => e !== id));
	};

	const moveState = (id, x, y) => {
		if (states.length <= 0) return;
		let index = utils.IDToIndex(states, id);
		if (index === -1) return;

		let newStates = [...states];
		newStates[index].x = x;
		newStates[index].y = y;

		setStates([...newStates]);
	};
	const moveTransition = (id, x, y) => {
		if (transitions.length <= 0) return;
		let index = utils.IDToIndex(transitions, id);
		if (index === -1) return;

		let newTransitions = [...transitions];
		newTransitions[index].x = x;
		newTransitions[index].y = y;

		setTransitions([...newTransitions]);
	};

	const removeState = (id) => {
		removeTransitions(id);
		setAcceptStatesIDs(acceptStatesIDs.filter((e) => e != id));
		setStates(states.filter((e) => e.id != id));
	};
	const removeTransitions = (id) => {
		const newTransitions = [];
		transitions.forEach((transition) => {
			if (transition.start !== id && transition.end !== id)
				newTransitions.push(transition);
		});
		setTransitions([...newTransitions]);
	};
	const removeTransition = (id) => {
		setTransitions(transitions.filter((t) => t.id != id));
	};
	const updateTransitions = (symbols) => {
		let newTransitions = transitions.map((transition) => {
			transition.symbols = transition.symbols.filter((symbol) =>
				symbols.includes(symbol)
			);
			if (transition.symbols.length > 0) {
				return transition;
			}
		});
		newTransitions = newTransitions.filter((t) => t);
		setTransitions([...newTransitions]);
	};

	// Supports non deterministic machines
	const recognizeString = (string, currentStateID) => {
		// La cadena se terminó de analizar
		// Y concluyó en un estado final
		if (string.length <= 0) {
			return isAcceptState(currentStateID);
		}

		const possibleTransitions = transitionsByIDAndSymbol(
			currentStateID,
			string[0]
		);

		string = string.substring(1);

		let accepted = false;
		let index = 0;
		while (!accepted && index < possibleTransitions.length) {
			accepted = recognizeString(string, possibleTransitions[index].end);

			index++;
		}

		return accepted;
	};

	const isAcceptState = (id) => {
		return acceptStatesIDs.includes(id);
	};

	const transitionsByIDAndSymbol = (id, symbol) => {
		let filteredTransitions = transitions.filter(
			(t) => t.start === id && t.symbols.includes(symbol)
		);
		return filteredTransitions;
	};
	const transitionByIDAndSymbol = (id, symbol) => {
		let transition;
		transitions.forEach((t) => {
			if (t.start === id && t.symbols.includes(symbol)) {
				transition = t;
			}
		});
		return transition;
	};

	const setMachine = (
		states,
		transitions,
		initialStateID,
		acceptStatesIDs,
		name
	) => {
		setName(name);
		setInitialStateID(initialStateID);
		setAcceptStatesIDs([...acceptStatesIDs]);
		setStates([...states]);
		setTransitions([...transitions]);
	};

	const value = {
		name,
		alphabet,
		states,
		transitions,
		initialStateID,
		acceptStatesIDs,
		addState,
		addTransition,
		renameMachine,
		renameState,
		renameTransition,
		setInitialState,
		addAcceptState,
		removeAcceptState,
		moveState,
		moveTransition,
		removeState,
		removeTransition,
		autoAlphabet,
		setAutoAlphabet,
		updateAlphabet,
		recognizeString,
		setMachine,
	};
	return (
		<MachineContext.Provider value={value}>
			{children}
		</MachineContext.Provider>
	);
}

// Assuming a deterministic machine
// const recognizeString = (string) => {

// 	let accepted = true;
// 	let currentStateID = initialStateID;

// 	while (string.length > 0 && accepted) {
// 		const transition = transitionByIDAndSymbol(
// 			currentStateID,
// 			string[0]
// 		);

// 		if (transition) {
// 			currentStateID = transitionByIDAndSymbol(
// 				currentStateID,
// 				string[0]
// 			).end;
// 			string = string.substring(1);

// 		} else {
// 			accepted = false;
// 		}
// 	}

// 	return isAcceptState(currentStateID) && accepted;
// };
